/* eslint-disable prettier/prettier */
import React, { Fragment } from 'react';
import moment from 'moment';
import { ShowMoreText } from '@jjj/components';
import { getFormattedDate } from '../../../helpers/dateTime';
import { NotificationList, InlineLink, SubBody, Feedback } from './notificationComponent.style';
import { t } from '../../../i18n';
import { isSystemAdminRoleOrHR } from '../../../helpers/userRoles';

const notification_types = {
  multiDay: 'new_multi_day_job',
  singleDay: 'new_single_day_job',
  workerNotEnroute: 'worker_not_enroute',

  workerAcceptsTheSameDayGig: 'worker_accepts_the_same_day_gig',
  workerNotClockedIn: 'worker_not_clocked_in',
  workerNotOnsite: 'worker_not_onsite',
  workerStartedWorkingLater: 'worker_started_working_later',
  workerAccreditationPending: 'worker_accreditation_pending',
  workerLeftJob: 'worker_left_job',
  workerBackgroundCheckComplete: 'worker_background_check_complete',
  workerBackgroundCheckStatusUpdate: 'worker_background_check_status_update',
  workerPromotionFailed: 'worker_promotion_failed',

  managerCanceledSingleDayJob: 'manager_canceled_single_day_job',
  managerCanceledMultiDayJob: 'manager_canceled_multi_day_job',

  workerRatedNever: 'worker_rated_never',

  jobFailedPaymentMethod: 'job_failed_payment_method',
  managerInPersonClockInOutOptIn: 'manager_in_person_clock_in_out_opt_in',

  managerAccountDeletedByUser: 'manager_account_deleted_by_user',

  newSingleDayPendingApprovalJob: 'new_single_day_pending_approval_job',
  newMultiDayPendingApprovalJob: 'new_multi_day_pending_approval_job',

  talentContestedHours: 'talent_contested_hours',
  agencyContestedHours: 'agency_contested_hours',

  contestedHoursEdited: 'contested_hours_edited',
  contestedHoursDenied: 'contested_hours_denied',
  contestedHoursResolved: 'contested_hours_resolved',

  flaggedTimesheet: 'flagged_timesheet',
  workerSubmissionsCreated: 'worker_submissions_created',
  workerSubmissionReviewed: 'worker_submission_reviewed',

  tenureApproaching: 'tenure_approaching',
  tenureQualified: 'tenure_qualified',
  tenureRecalculated: 'tenure_recalculated',

  conversionEligible: 'conversion_eligible',
  conversionInitiated: 'conversion_initiated',
  conversionFailed: 'conversion_failed',
  conversionCompleted: 'conversion_completed',
};

const NOTIFICATIONS_CENTRE_URL = '/notifications?tab=active';

const getAgencyWorkerUrl = meta => {
  const workerIdentityId = meta?.worker_identity?.id || '';
  const agencyId = meta?.agency_id || meta?.agency?.id;

  return agencyId ? `/agencyWorkers/${workerIdentityId}?tab=${agencyId}` : `/agencyWorkers/${workerIdentityId}`;
};

const isDroppedWithin15minsOfAccepting = (accepted_at, canceled_at) => {
  const duration = moment.duration(moment(accepted_at).diff(moment(canceled_at)));
  const minutes = Math.abs(duration.asMinutes());

  return minutes <= 15;
};

const getHoursBetweenDates = (job_starts_at, canceled_at) => {
  const duration = moment.duration(moment(canceled_at).diff(moment(job_starts_at)));
  const hours = duration.asHours().toFixed(1);

  return Math.abs(hours);
};

export function getBody(notification, role) {
  const { notification_type, meta } = notification.notification;
  const notAvailable = t('dash.common.aliases.n/a');
  const {
    accepted_at,
    accreditation_name,
    accreditation_status,
    canceled_at,
    company_name,
    delay,
    expiration_date,
    first_name,
    job_adress,
    job_id,
    booking_starts_at,
    last_name,
    no_worker_assigned_shift_id,
    position_name,
    state,
    worker_full_name,
    worker_id,
    package_name,
    manager_full_name,
    workers_count,
    shifts_count,
    hours_before_start,
    num_days,
    booker_name,
    starts_at,
    shift_id,
    feedback,
    job_state,
    rated_by_full_name,
    reasons,
    manager_name,
    deleted_manager_email,
    placeholder_manager_email,
    talent_name,
    unit_name,
    job_address,
    date,
  } = meta;

  switch (notification_type) {
    case notification_types.singleDay:
    case notification_types.multiDay:
    case notification_types.newSingleDayPendingApprovalJob:
    case notification_types.newMultiDayPendingApprovalJob:
      return (
        <div>
          <NotificationList>
            {meta.bookings.map((item, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <span key={index}>
                {t('dash.notificationsCenter.bodies.new_job_created_message', {
                  workers_required: item.workers_required,
                  position_name: item.position_name,
                  skill_level: item.skill_level,
                  starts_at: getFormattedDate({
                    date: item.starts_at,
                    formatKey: 'time_hours_minutes_long',
                  }).toUpperCase(),
                  finishes_at: getFormattedDate({
                    date: item.finishes_at,
                    formatKey: 'time_hours_minutes_long',
                  }).toUpperCase(),
                })}
              </span>
            ))}
          </NotificationList>
        </div>
      );

    case notification_types.managerAccountDeletedByUser: {
      return (
        <div>
          {t(`dash.notificationsCenter.bodies.${notification_type}`, {
            deletedManagerEmail: deleted_manager_email,
            companyName: company_name,
            placeholderManagerEmail: placeholder_manager_email,
          })}
        </div>
      );
    }

    case notification_types.workerAccreditationPending: {
      return (
        <div>
          {t(`dash.notificationsCenter.bodies.${notification_type}`, {
            accreditation_name,
            accreditation_status,
            first_name,
            last_name,
            state,
          })}
          <SubBody>
            {t('dash.notificationsCenter.bodies.expiry_label')}:{' '}
            {getFormattedDate({ date: expiration_date, formatKey: 'date_numeric_long', noLocalTimeZone: true })}
          </SubBody>
        </div>
      );
    }

    case notification_types.workerNotEnroute:
      return (
        <div>
          <span>{t(`dash.notificationsCenter.bodies.${notification_type}`)}</span>
        </div>
      );

    case notification_types.workerAcceptsTheSameDayGig:
      return <div>{t(`dash.notificationsCenter.bodies.${notification_type}`, { worker_full_name, company_name })}</div>;

    case notification_types.workerNotOnsite:
      return (
        <div>
          {t(`dash.notificationsCenter.bodies.${notification_type}`, {
            worker_full_name,
            company_name,
            position_name,
            job_adress,
          })}
        </div>
      );

    case notification_types.workerNotClockedIn:
      return <div>{t(`dash.notificationsCenter.bodies.${notification_type}`, { worker_full_name, company_name })}</div>;

    case notification_types.workerStartedWorkingLater:
      return (
        <div>
          {t(`dash.notificationsCenter.bodies.${notification_type}`, { worker_full_name, company_name, delay })}
        </div>
      );

    case notification_types.workerBackgroundCheckComplete:
      return <div>{t(`dash.notificationsCenter.bodies.${notification_type}`, { worker_full_name, package_name })}</div>;

    case notification_types.workerBackgroundCheckStatusUpdate:
      return <div>{t(`dash.notificationsCenter.bodies.${notification_type}`, { worker_full_name, package_name })}</div>;

    case notification_types.workerPromotionFailed:
      return <div>{t(`dash.notificationsCenter.bodies.${notification_type}`)}</div>;

    case notification_types.workerLeftJob: {
      const hours = getHoursBetweenDates(booking_starts_at, canceled_at);
      const stateLabel = state ? t('dash.notificationsCenter.bodies.state_label', { state }) : '';

      return (
        <div>
          <InlineLink to={`/talent/${worker_id}`}>{worker_full_name}</InlineLink>{' '}
          {t('dash.notificationsCenter.dropped')}{' '}
          <InlineLink to={`/jobs/${job_id}?tab=bookings&scrollTo=${no_worker_assigned_shift_id}`}>
            {company_name}
          </InlineLink>{' '}
          {t(`dash.notificationsCenter.bodies.${notification_type}`, { stateLabel, hours })}
          <SubBody>
            {t('dash.notificationsCenter.accepted_label')}:{' '}
            {accepted_at ?
              getFormattedDate({ date: accepted_at, formatKey: 'datetime_date_numeric' }) : notAvailable}{' '}
            <br />
            {t('dash.notificationsCenter.dropped_label')}:{' '}
            {canceled_at ?
              getFormattedDate({ date: canceled_at, formatKey: 'datetime_date_numeric' }) : notAvailable}{' '}
            <br />
            {t('dash.notificationsCenter.shift_start_label')}:{' '}
            {booking_starts_at
              ? getFormattedDate({ date: booking_starts_at, formatKey: 'datetime_date_numeric' })
              : notAvailable}
          </SubBody>
        </div>
      );
    }

    case notification_types.managerCanceledSingleDayJob:
      return (
        <div>
          {shifts_count
            ? t(`dash.notificationsCenter.bodies.${notification_type}`, {
                manager_full_name,
                shifts_count,
                hours_before_start,
              })
            : t(`dash.notificationsCenter.bodies.${notification_type}_without_workers`, {
                manager_full_name,
                hours_before_start,
              })}
        </div>
      );
    case notification_types.managerCanceledMultiDayJob:
      return (
        <div>
          {workers_count
            ? t(`dash.notificationsCenter.bodies.${notification_type}`, {
                manager_full_name,
                num_days,
                workers_count,
                hours_before_start,
              })
            : t(`dash.notificationsCenter.bodies.${notification_type}_without_workers`, {
                manager_full_name,
                num_days,
                hours_before_start,
              })}
        </div>
      );

    case notification_types.workerRatedNever:
      return (
        <div>
          <InlineLink to={`/talent/${worker_id}`}>{worker_full_name}</InlineLink>{' '}
          {t('dash.notificationsCenter.bodies.worker_rated_by_message', { rated_by_full_name })}{' '}
          <InlineLink to={`/jobs/${job_id}?tab=bookings&scrollTo=${shift_id}`}>{company_name}</InlineLink>{' '}
          {t('dash.notificationsCenter.bodies.worker_rate_booking_message', {
            job_state,
            booking_starts_at: getFormattedDate({ date: booking_starts_at, formatKey: 'datetime_date_numeric' }),
          })}{' '}
          {t('dash.notificationsCenter.bodies.worker_rate_reasons_message', {
            reasons: reasons || notAvailable,
          })}{' '}
          {feedback && (
            <Fragment>
              {t('dash.notificationsCenter.bodies.worker_rate_feedback_message')}
              <SubBody>
                <Feedback>
                  <ShowMoreText
                    lines={2}
                    anchorClass="showMoreTextButton"
                    less={t('dash.common.button.show_less_label')}
                    more={t('dash.common.button.read_more_label')}
                  >
                    {feedback}
                  </ShowMoreText>
                </Feedback>
              </SubBody>
            </Fragment>
          )}
        </div>
      );

    case notification_types.jobFailedPaymentMethod:
      return (
        <div>
          {t(`dash.notificationsCenter.bodies.${notification_type}`, {
            booker_name,
            company_name,
            starts_at: getFormattedDate({ date: starts_at, formatKey: 'date_numeric_long' }),
          })}
        </div>
      );

    case notification_types.managerInPersonClockInOutOptIn:
      return (
        <div>
          {t(`dash.notificationsCenter.bodies.${notification_type}`, {
            managerName: manager_name,
            companyName: company_name,
          })}
        </div>
      );

    case notification_types.talentContestedHours:
      return (
        <div>
          {t(`dash.notificationsCenter.bodies.${notification_type}`, {
            talent_name,
            unit_name,
            job_address,
            date: getFormattedDate({ date, formatKey: 'date_numeric_long', noLocalTimeZone: true }),
          })}
        </div>
      );

    case notification_types.agencyContestedHours:
      return (
        <div>
          {t(`dash.notificationsCenter.bodies.${notification_type}`, {
            agency_name: meta?.agency_name,
            unit_name,
            job_address,
            date: getFormattedDate({ date, formatKey: 'date_numeric_long', noLocalTimeZone: true }),
          })}
        </div>
      );

    case notification_types.contestedHoursEdited:
    case notification_types.contestedHoursDenied:
      return (
        <div>
          {t(`dash.notificationsCenter.bodies.${notification_type}`, {
            resolved_by: meta?.resolved_by_name,
            job_address,
          })}
        </div>
      );

    case notification_types.contestedHoursResolved:
      return (
        <div>
          {t(`dash.notificationsCenter.bodies.${notification_type}`, {
            resolved_by: meta?.resolved_by_name,
            job_address,
          })}
        </div>
      );

    case notification_types.tenureApproaching:
      return (
        <div>
          <InlineLink to={getAgencyWorkerUrl(meta)}>{meta.worker_identity?.full_name || ''}</InlineLink>{' '}
          {t(`dash.notificationsCenter.bodies.${notification_type}`, {
            weeks_number: meta.weeks_number,
          })}
        </div>
      );

    case notification_types.tenureQualified:
      return (
        <div>
          <InlineLink to={getAgencyWorkerUrl(meta)}>{meta.worker_identity?.full_name || ''}</InlineLink>{' '}
          {t(`dash.notificationsCenter.bodies.${notification_type}`, {
            weeks_number: meta.weeks_number,
            next_weeks_number: Number(meta.weeks_number) + 1,
          })}
        </div>
      );

    case notification_types.tenureRecalculated:
      return (
        <div>
          <InlineLink to={getAgencyWorkerUrl(meta)}>{meta.worker_identity?.full_name || ''}</InlineLink>{' '}
          {t(`dash.notificationsCenter.bodies.${notification_type}`)}
        </div>
      );

    case notification_types.conversionEligible:
      return (
        <div>
          <InlineLink to={getAgencyWorkerUrl(meta)}>{meta.worker_identity?.full_name || ''}</InlineLink>{' '}
          {t(`dash.notificationsCenter.bodies.${notification_type}`, {
            weeks_number: meta.weeks_number,
          })}{' '}
          <InlineLink to={getAgencyWorkerUrl(meta)}>
            {t('dash.notificationsCenter.bodies.review_permanent_conversion_cta')}
          </InlineLink>
        </div>
      );

    case notification_types.conversionInitiated:
      return (
        <div>
          {isSystemAdminRoleOrHR(role) ? (
            <InlineLink to={`/agencyWorkers/${meta.worker_identity?.id}`}>
              {meta.worker_identity?.full_name || ''}
            </InlineLink>
          ) : (
            meta.worker_identity?.full_name || ''
          )}{' '}
          {t(`dash.notificationsCenter.bodies.${notification_type}`)}
        </div>
      );

    case notification_types.conversionFailed:
      return (
        <div>
          <InlineLink to={getAgencyWorkerUrl(meta)}>{meta.worker_identity?.full_name || ''}</InlineLink>{' '}
          {t(`dash.notificationsCenter.bodies.${notification_type}`)}
        </div>
      );

    case notification_types.conversionCompleted:
      return (
        <div>
          {t(`dash.notificationsCenter.bodies.${notification_type}_opener`)}{' '}
          <InlineLink to={getAgencyWorkerUrl(meta)}>{meta.worker_identity?.full_name || ''}</InlineLink>{' '}
          {t(`dash.notificationsCenter.bodies.${notification_type}`)}
        </div>
      );

    default:
      return notification.body;
  }
}

export function getTitle(notification) {
  const notAvailable = t('dash.common.aliases.n/a');
  const { notification_type, meta } = notification.notification;

  switch (notification_type) {
    case notification_types.singleDay:
      return t(`dash.notificationsCenter.titles.${notification_type}`, {
        company_name: meta.company_name || notAvailable,
        starts_at: getFormattedDate({ date: meta.starts_at, formatKey: 'date_day_month' }),
      });

    case notification_types.multiDay:
      return t(`dash.notificationsCenter.titles.${notification_type}`, {
        company_name: meta.company_name || notAvailable,
        starts_at: getFormattedDate({ date: meta.starts_at, formatKey: 'date_day_month' }),
        finishes_at: getFormattedDate({ date: meta.finishes_at, formatKey: 'date_day_month' }),
      });

    case notification_types.managerInPersonClockInOutOptIn:
      return t(`dash.notificationsCenter.titles.${notification_type}`);

    case notification_types.workerAccreditationPending: {
      const { accreditation_name, first_name, last_name } = meta;

      return t(`dash.notificationsCenter.titles.${notification_type}`, { accreditation_name, first_name, last_name });
    }

    case notification_types.workerNotEnroute:
    case notification_types.workerPromotionFailed:
      return t(`dash.notificationsCenter.titles.${notification_type}`, { fullName: meta.worker_full_name });

    case notification_types.newSingleDayPendingApprovalJob:
    case notification_types.newMultiDayPendingApprovalJob:
      return t(`dash.notificationsCenter.titles.${notification_type}`, { company_name: meta.company_name });

    case notification_types.workerLeftJob: {
      if (isDroppedWithin15minsOfAccepting(meta.accepted_at, meta.canceled_at)) {
        return t('dash.notificationsCenter.titles.dropped_shift');
      }

      return t(`dash.notificationsCenter.titles.${notification_type}`);
    }

    case notification_types.managerCanceledSingleDayJob:
    case notification_types.managerCanceledMultiDayJob:
    case notification_types.workerAcceptsTheSameDayGig:
    case notification_types.workerNotOnsite:
    case notification_types.workerNotClockedIn:
    case notification_types.workerStartedWorkingLater:
    case notification_types.workerBackgroundCheckComplete:
    case notification_types.workerBackgroundCheckStatusUpdate:
    case notification_types.workerRatedNever:
    case notification_types.jobFailedPaymentMethod:
    case notification_types.managerAccountDeletedByUser:
    case notification_types.contestedHoursEdited:
    case notification_types.contestedHoursDenied:
    case notification_types.contestedHoursResolved:
      return t(`dash.notificationsCenter.titles.${notification_type}`);

    case notification_types.talentContestedHours:
    case notification_types.agencyContestedHours:
      return t(`dash.notificationsCenter.titles.${notification_type}`);
    case notification_types.workerSubmissionsCreated: {
      const { agency_name } = meta;

      return t(`dash.notificationsCenter.titles.${notification_type}`, { agency_name });
    }
    case notification_types.workerSubmissionReviewed: {
      const { resolved_by_name, job_address } = meta;

      return t(`dash.notificationsCenter.titles.${notification_type}`, { resolved_by_name, job_address });
    }
    case notification_types.flaggedTimesheet:
      return t(`dash.notificationsCenter.titles.${notification_type}`);

    case notification_types.tenureApproaching:
    case notification_types.tenureQualified:
    case notification_types.tenureRecalculated:
      return null;

    case notification_types.conversionEligible:
    case notification_types.conversionInitiated:
    case notification_types.conversionFailed:
    case notification_types.conversionCompleted:
      return t(`dash.notificationsCenter.titles.${notification_type}`);

    default:
      return notification.title;
  }
}

export function getLink(notification, role) {
  const { notification_type, meta } = notification.notification;
  const jobLinksFor = [
    notification_types.workerAcceptsTheSameDayGig,
    notification_types.workerNotClockedIn,
    notification_types.workerNotOnsite,
    notification_types.workerStartedWorkingLater,
    notification_types.singleDay,
    notification_types.workerNotEnroute,
    notification_types.jobFailedPaymentMethod,
    notification_types.workerSubmissionsCreated,
  ];

  const { job_id, worker_id, accreditation_submission_id, company_level, company_id, job_group_id } = meta;

  if (jobLinksFor.includes(notification_type) && job_id) {
    return `/jobs/${job_id}`;
  }

  switch (notification_type) {
    case notification_types.workerPromotionFailed:
    case notification_types.workerBackgroundCheckComplete:
    case notification_types.workerBackgroundCheckStatusUpdate:
      return `/applicants/${worker_id}/`;

    case notification_types.workerAccreditationPending:
      return `/talent/${worker_id}/talentAccreditations/${accreditation_submission_id}`;

    case notification_types.managerInPersonClockInOutOptIn:
      if (company_level === 'unit') {
        return `/units/${company_id}`;
      }

      if (company_level === 'subunit') {
        return `/subUnits/${company_id}`;
      }

      return '';

    case notification_type === notification_types.multiDay && job_group_id: {
      return `/jobs/multiDay?tab=futures&job_group_id=${job_group_id}`;
    }

    case notification_types.managerCanceledSingleDayJob:
      // eslint-disable-next-line max-len
      return `/jobs/${job_id}?page=1&tab=logs&trigger[]=operator_cancelation&trigger[]=accept_ma_gig&trigger[]=accept_admin_gig`;

    case notification_types.managerCanceledMultiDayJob:
      // eslint-disable-next-line max-len
      return `/jobs/multiDay?tab=future&job_group_id=${job_group_id}&page=1&trigger[]=operator_cancelation&trigger[]=accept_ma_gig&trigger[]=accept_admin_gig`;

    case notification_types.talentContestedHours:
    case notification_types.agencyContestedHours:
      return '/timesheets?subtab=pending';

    case notification_types.contestedHoursResolved:
    case notification_types.contestedHoursEdited:
    case notification_types.contestedHoursDenied:
      return '/timesheets?subtab=resolved';

    case notification_types.workerSubmissionReviewed:
      return '/timesheets';

    case notification_types.flaggedTimesheet:
      return '/timesheets';

    case notification_types.conversionInitiated:
      if (!isSystemAdminRoleOrHR(role)) {
        return NOTIFICATIONS_CENTRE_URL;
      }

      return `/agencyWorkers/${meta.worker_identity?.id || ''}`;

    case notification_types.tenureApproaching:
    case notification_types.tenureQualified:
    case notification_types.tenureRecalculated:
    case notification_types.conversionEligible:
    case notification_types.conversionFailed:
    case notification_types.conversionCompleted:
      return getAgencyWorkerUrl(meta);

    default:
      return '';
  }
}
